// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
// RapidRailsUI JS entry — collision guard + bulk controller registration.
//
// Importmap hosts: this file is host-local but its `import` specifiers below
// are bare paths like "controllers/rapid_rails_ui/accordion_controller", which
// `importmap-rails` resolves via the pins `pin_all_from RapidRailsUI::Engine.root.join(...)`
// generated by the install generator. That means a `bundle update rapid_rails_ui`
// propagates fixes to existing controllers automatically.
//
// Jsbundling/esbuild hosts: the install generator also copies controllers into
// `app/javascript/controllers/rapid_rails_ui/`, and these same bare specifiers
// resolve to those local copies. The generator overwrites those copies every
// run, so the failure mode is "host didn't run the generator after bundle
// update" rather than "host bumped and forgot to re-copy file-by-file".
//
// Usage:
//   import { Application } from "@hotwired/stimulus"
//   import { guardCollisions, registerAll } from "rapid_rails_ui/stimulus_guard"
//
//   const application = Application.start()
//   guardCollisions(application)
//   registerAll(application, { prefix: "rui" })
//
// See lib/rapid_rails_ui/docs/stimulus_guard.md in the gem for prefix/only/except patterns.
//
// Failure reporting (v0.73.0): registerAll wraps each registration so one
// controller that throws is reported as "[RapidRailsUI] could not register
// <id>" and the rest still register; it returns the identifiers it registered
// so the managed index.js block can hand them to watchConnections.

import { ruiLog } from "../controllers/rapid_rails_ui/shared/diagnostics"
import AccordionController from "../controllers/rapid_rails_ui/accordion_controller"
import AlertController from "../controllers/rapid_rails_ui/alert_controller"
import AutoSubmitController from "../controllers/rapid_rails_ui/auto_submit_controller"
import BadgeController from "../controllers/rapid_rails_ui/badge_controller"
import CarouselController from "../controllers/rapid_rails_ui/carousel_controller"
import ChartController from "../controllers/rapid_rails_ui/chart_controller"
import CheckboxController from "../controllers/rapid_rails_ui/checkbox_controller"
import CheckboxSwitchController from "../controllers/rapid_rails_ui/checkbox_switch_controller"
import ClipboardController from "../controllers/rapid_rails_ui/clipboard_controller"
import ComboboxController from "../controllers/rapid_rails_ui/combobox_controller"
import DatePickerController from "../controllers/rapid_rails_ui/date_picker_controller"
import DialogController from "../controllers/rapid_rails_ui/dialog_controller"
import EditableController from "../controllers/rapid_rails_ui/editable_controller"
import InputValidationController from "../controllers/rapid_rails_ui/input_validation_controller"
import KanbanController from "../controllers/rapid_rails_ui/kanban_controller"
import LiveSearchController from "../controllers/rapid_rails_ui/live_search_controller"
import MenuController from "../controllers/rapid_rails_ui/menu_controller"
import PaginationJumperController from "../controllers/rapid_rails_ui/pagination_jumper_controller"
import RangeController from "../controllers/rapid_rails_ui/range_controller"
import StepsController from "../controllers/rapid_rails_ui/steps_controller"
import SubmenuController from "../controllers/rapid_rails_ui/submenu_controller"
import TableController from "../controllers/rapid_rails_ui/table_controller"
import TabsController from "../controllers/rapid_rails_ui/tabs_controller"
import TextareaValidationController from "../controllers/rapid_rails_ui/textarea_validation_controller"
import TimePickerController from "../controllers/rapid_rails_ui/time_picker_controller"
import UploadController from "../controllers/rapid_rails_ui/upload_controller"

import CssHighlightNavController from "../controllers/rapid_rails_ui/shared/css_highlight_nav_controller"
import KeyboardController from "../controllers/rapid_rails_ui/shared/keyboard_controller"
import PopupController from "../controllers/rapid_rails_ui/shared/popup_controller"
import RecentSearchesController from "../controllers/rapid_rails_ui/shared/recent_searches_controller"
import SearchController from "../controllers/rapid_rails_ui/shared/search_controller"
import SwitchController from "../controllers/rapid_rails_ui/shared/switch_controller"
import VoiceSearchController from "../controllers/rapid_rails_ui/shared/voice_search_controller"

// Bare-id registry. `internal: true` entries are always registered under
// "rapid-rails-ui--<id>" regardless of `prefix` — these controllers are wired
// from inside the gem's own component templates and must not be re-namespaced.
const REGISTRY = [
  { id: "accordion", controller: AccordionController },
  { id: "alert", controller: AlertController },
  { id: "auto-submit", controller: AutoSubmitController },
  { id: "badge", controller: BadgeController },
  { id: "carousel", controller: CarouselController },
  { id: "chart", controller: ChartController },
  { id: "checkbox", controller: CheckboxController },
  { id: "checkbox-switch", controller: CheckboxSwitchController },
  { id: "clipboard", controller: ClipboardController },
  { id: "combobox", controller: ComboboxController },
  { id: "date-picker", controller: DatePickerController },
  { id: "dialog", controller: DialogController },
  { id: "editable", controller: EditableController },
  { id: "input-validation", controller: InputValidationController },
  { id: "kanban", controller: KanbanController },
  { id: "live-search", controller: LiveSearchController },
  { id: "menu", controller: MenuController },
  { id: "pagination-jumper", controller: PaginationJumperController, internal: true },
  { id: "range", controller: RangeController },
  { id: "steps", controller: StepsController },
  { id: "submenu", controller: SubmenuController },
  { id: "table", controller: TableController },
  { id: "tabs", controller: TabsController },
  { id: "textarea-validation", controller: TextareaValidationController },
  { id: "time-picker", controller: TimePickerController },
  { id: "upload", controller: UploadController },
  { id: "css-highlight-nav", controller: CssHighlightNavController },
  { id: "keyboard", controller: KeyboardController },
  { id: "popup", controller: PopupController },
  { id: "recent-searches", controller: RecentSearchesController },
  { id: "search", controller: SearchController },
  { id: "switch", controller: SwitchController },
  { id: "voice-search", controller: VoiceSearchController }
]

export const RAPID_RAILS_UI_CONTROLLER_IDS = REGISTRY.map(e => e.id)

// Wrap application.register so a duplicate identifier registration fails fast
// at boot. The error message names the colliding id AND identifies the winning
// controller class so you can locate the duplicate quickly. Useful when
// `stimulus_namespace` is in use and a host app may otherwise end up with two
// controllers under the same id.
export function guardCollisions(application) {
  const seen = new Map()
  const original = application.register.bind(application)
  application.register = (id, controller) => {
    if (seen.has(id)) {
      const winner = seen.get(id)
      // The SAME controller class arriving twice is not a collision. Importmap
      // apps hit this by default: eagerLoadControllersFrom("controllers") loads
      // the copied controllers/rapid_rails_ui/pagination_jumper_controller as
      // "rapid-rails-ui--pagination-jumper", then registerAll registers that
      // same module under the same id. Skip the repeat. RRUI-015.
      if (winner === controller) return

      const winnerName = winner?.name || "(anonymous)"
      const incomingName = controller?.name || "(anonymous)"
      throw new Error(
        `Stimulus identifier collision: "${id}" was already registered by ${winnerName}; ` +
        `attempted re-registration by ${incomingName}. ` +
        `Resolve by passing { except: ["${id.split("--").pop()}"] } to registerAll() or by renaming one controller.`
      )
    }
    seen.set(id, controller)
    return original(id, controller)
  }
  return application
}

// Register every RapidRailsUI Stimulus controller on the given application.
//
// Options:
//   prefix  — string. Stimulus id namespace. "rui" → controllers register as
//             "rui--accordion", "rui--dialog", etc. Default null (bare ids).
//             Internal controllers (registry.internal === true) always use
//             "rapid-rails-ui--<id>" regardless of prefix.
//   only    — array of bare ids. Register only these. Mutually-non-exclusive
//             with except (except wins on overlap).
//   except  — array of bare ids. Skip these. Use to provide your own
//             implementation: registerAll(app, { except: ["dialog"] }); then
//             application.register("rui--dialog", MyDialog).
//
// Returns the array of full identifiers it registered.
export function registerAll(application, { prefix = null, only = null, except = null } = {}) {
  const skip = new Set(except || [])
  const include = only ? new Set(only) : null
  const registered = []

  for (const { id, controller, internal } of REGISTRY) {
    if (include && !include.has(id)) continue
    if (skip.has(id)) continue

    let fullId
    if (internal) {
      fullId = `rapid-rails-ui--${id}`
    } else if (prefix) {
      fullId = `${prefix}--${id}`
    } else {
      fullId = id
    }
    try {
      application.register(fullId, controller)
      registered.push(fullId)
    } catch (cause) {
      ruiLog("stimulus_guard", `could not register ${fullId}; that component's behaviour is off on this page`, { cause })
    }
  }
  return registered
}
